import { DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { APP_CONFIG } from '../../app.config';
import { StoreRegisterType } from '../../shared/enums/store-register-type.enum';
import { UtilHelper } from '../../shared/helpers/utils.helper';
import { Coupon, CouponValidate } from '../../shared/models/coupon.model';
import { Error } from '../../shared/models/error.model';
import { Pendency } from '../../shared/models/pendency.model';
import { Plan } from '../../shared/models/plan.model';
import { ShortLink } from '../../shared/models/shortlink.model';
import { RequestService } from '../../shared/services/request.service';
import { BusinessHour } from './models/business-hour.model';
import { Store, StoreAddressForm, StoreDataForm } from './models/store.model';
import { Table, TableListModel } from './models/table-list.model';

@Injectable()
export class StoreService {

	constructor(
		private decimalPipe: DecimalPipe,
		private requestService: RequestService
	) { }

	getZipCode(zipCode: string): Promise<any> {
		return new Promise((resolve, reject) => {
			this.requestService.getData("https://viacep.com.br/ws/" + zipCode + "/json/").then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	validateCnpj(cnpj: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const cnpjUnmasked: string = UtilHelper.unmask(cnpj);
			this.requestService.getData(APP_CONFIG.WEBSERVICE.VALIDATE_CNPJ + cnpjUnmasked).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	validateEmail(email: string): Promise<void> {
		return new Promise((resolve, reject) => {
			this.requestService.getData(APP_CONFIG.WEBSERVICE.VALIDATE_EMAIL + email).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getData(estabId: number): Promise<Store> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ estabId: estabId }, APP_CONFIG.WEBSERVICE.GET_ESTABLISHMENT_DATA);
			this.requestService.getData(urlRequest).then((response: Store) => {
				console.log(response);
				resolve(new Store(response));
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateIdentity(estabIdentity: any): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = JSON.parse(JSON.stringify(estabIdentity));
			this.requestService.postData(APP_CONFIG.WEBSERVICE.UPDATE_STORE_IDENTITY, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateBusinessModel(store: Store): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = {
				id: store.id,
				businessType: store.businessType,
				acceptDelivery: store.acceptDelivery,
				offerPickup: store.offerPickup,
				optionForHere: store.optionForHere,
				offerDriveIn: store.offerDriveIn
			}
			this.requestService.putData(APP_CONFIG.WEBSERVICE.UPDATE_BUSINESS_MODEL, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateBusinessPayments(store: Store): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = {
				id: store.id,
				allowPayLater: store.allowPayLater || false,
				allowOnlinePayment: store.allowOnlinePayment || false
			}
			this.requestService.putData(APP_CONFIG.WEBSERVICE.UPDATE_BUSINESS_PAYMENTS, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateStoreData(storeData: StoreDataForm): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = JSON.parse(JSON.stringify(storeData));
			data.cnpj = UtilHelper.unmask(data.cnpj);
			data.establishmentPhone = UtilHelper.unmask(data.establishmentPhone);
			data.contactMobilePhone = UtilHelper.unmask(data.contactMobilePhone);
			data.cpf = UtilHelper.unmask(data.cpf);
			this.requestService.putData(APP_CONFIG.WEBSERVICE.UPDATE_STORE_DATA, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updateStoreAddress(storeId: number, storeAddress: StoreAddressForm): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = JSON.parse(JSON.stringify(storeAddress));
			data.zipCode = UtilHelper.unmask(data.zipCode);
			const urlRequest: string = this.requestService.buildUrlPathParams({ storeId: storeId }, APP_CONFIG.WEBSERVICE.UPDATE_STORE_ADDRESS);
			this.requestService.putData(urlRequest, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	updatePendencyStore(storeId: number, pendency: Pendency): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = pendency.toUpdateJson(storeId, "OK");
			this.requestService.putData(APP_CONFIG.WEBSERVICE.PENDENCIES, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	registerNewStore(store: Store, selectedPlan: Plan, password: string, captchaResponse: string, coupon?: Coupon): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = APP_CONFIG.WEBSERVICE.SAVE_STORE;
			const data: any = {
				store: {
					tradeName: store.tradeName,
					//cnpj: UtilHelper.unmask(store.cnpj),
					howKnow: store.howKnow,
					planId: selectedPlan.id,
					extraModulesId: this.getExtraModulesId(selectedPlan),
					coupon: coupon ? coupon.code : null
				},
				user: {
					name: store.contactName,
					username: store.contactEmail,
					phone: UtilHelper.unmask(store.contactMobilePhone),
					password: password
				}
			};
			console.log(JSON.stringify(data));
			if (captchaResponse) {
				this.requestService.addParamsHeader("g-recaptcha-response", captchaResponse);
			}
			this.requestService.postData(urlRequest, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	preregister(tradeName: string, contactName: string, phone: string, email: string, howKnow?: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const data: any = {
				tradeName: tradeName, 
				contactName: contactName,
				phone: UtilHelper.unmask(phone),
				email: email,
				howKnow: howKnow || "",
				countryPhoneCode: "55"
			}
			this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_PRE_STORE, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	register(store: Store, type: StoreRegisterType, selectedPlan?: Plan, coupon?: Coupon): Promise<any> {
		return new Promise((resolve, reject) => {
			let urlRequest: string;

			switch (type) {
				case StoreRegisterType.BRANCH:
					urlRequest = APP_CONFIG.WEBSERVICE.SAVE_BRANCH;
					break;

				case StoreRegisterType.PLACE_STORE:
					urlRequest = APP_CONFIG.WEBSERVICE.SAVE_PLACE_STORE;
					break;
			}
			const data: any = JSON.parse(JSON.stringify(store));
			data.cnpj = UtilHelper.unmask(data.cnpj);
			data.establishmentPhone = UtilHelper.unmask(data.establishmentPhone);
			data.contactMobilePhone = UtilHelper.unmask(data.contactMobilePhone);
			data.cpf = UtilHelper.unmask(data.cpf);
			data.zipCode = UtilHelper.unmask(data.zipCode);

			if (selectedPlan) {
				data.planId = selectedPlan.id;
				data.extraModulesId = this.getExtraModulesId(selectedPlan);
			}
			if (coupon) {
				data.coupon = coupon.code;
			}
			// Remove dados que não são do model
			delete data.fullAddress;
			delete data.featureInTest;

			console.log(JSON.stringify(data));
			this.requestService.postData(urlRequest, data).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	createPaymentId(storeId: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ storeId: storeId }, APP_CONFIG.WEBSERVICE.CREATE_STORE_PAYMENT_ID);
			this.requestService.postData(urlRequest).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	private getExtraModulesId(selectedPlan: Plan): Array<number> {
		const extras: Array<number> = selectedPlan.extraModules.filter((m) => m.selected).map((m) => m.id);
		if (extras && extras.length > 0) {
			return extras;
		}
		return null;
	}

	validateCoupon(code: string, translateService: TranslateService): Promise<any> {
		return new Promise((resolve, reject) => {
			this.requestService.getData(APP_CONFIG.WEBSERVICE.VALIDATE_COUPON + code).then((couponValidate: CouponValidate) => {
				console.log(couponValidate);
				if (couponValidate) {
					if (couponValidate.success) {
						const coupon: Coupon = new Coupon().toCouponFromValidate(couponValidate);
						resolve({
							success: true,
							coupon: coupon
						});
					} else {
						let complementMessage: string = "";
						if (couponValidate.couponMessage && couponValidate.couponMessage === "COUPON.COUPON_MINIMUM_VALUE_NOT_ACHIEVED") {
							complementMessage += couponValidate.minimunValueForUse ? (": R$ " + this.decimalPipe.transform(couponValidate.minimunValueForUse, '.2-2') + ".") : "";
						}
						resolve({
							success: false,
							message: translateService.instant(couponValidate.couponMessage) + complementMessage
						});
					}
				} else {
					resolve({
						success: false,
						message: "COUPON.COUPON_NOT_FOUND"
					});
				}
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	checkPendencies(estabId: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ estabId: estabId }, APP_CONFIG.WEBSERVICE.CHECK_PENDENCIES);
			this.requestService.getData(urlRequest).then((response: Array<Pendency>) => {
				const pendencies: Array<Pendency> = [];
				if (response && response.length > 0) {
					response.forEach((p: Pendency) => {
						const pendency: Pendency = new Pendency(p);
						if (pendency.pendingType) {
							pendencies.push(pendency);
						}
					});
				}
				pendencies.sort((p1, p2) => {
					return p1.pendingType['order'] > p2.pendingType['order'] ? 1 : -1;
				});
				resolve(pendencies);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	changeStatus(estabId: number, statusCode: string): Promise<void> {
		return new Promise((resolve, reject) => {
			const data: any = {
				id: estabId,
				status: statusCode
			};
			this.requestService.putData(APP_CONFIG.WEBSERVICE.CHANGE_ESTAB_STATUS, data).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	openClose(storeId: number, action: "OPEN" | "CLOSE"): Promise<void> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = action === "OPEN" ? APP_CONFIG.WEBSERVICE.OPEN : APP_CONFIG.WEBSERVICE.CLOSE;
			this.requestService.postData(urlRequest + storeId).then(() => {
				resolve();
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	async saveShortLink(shortLink: ShortLink, isPlace: boolean): Promise<any> {
		const data: any = {
			destination: shortLink.destination,
			slashtag: shortLink.newSlashtag,
			isPlace: isPlace
		};
		if (isPlace) {
			data.placeId = shortLink.placeId;
		} else {
			data.storeId = shortLink.storeId;
		}
		if (shortLink.id) {
			data.oldSlashtag = shortLink.slashtag;
			await this.requestService.putData(APP_CONFIG.WEBSERVICE.SAVE_SHORT_LINK, data);
		} else {
			await this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_SHORT_LINK, data);
		}
	}

	checkRemainingDaysTrial(storeId: number): Promise<any> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ storeId: storeId }, APP_CONFIG.WEBSERVICE.CHECK_REMAINING_DAYS_TRIAL);
			this.requestService.getData(urlRequest).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	getStates(): Array<any> {
		return [
			{ key: "AC", value: "Acre" },
			{ key: "AL", value: "Alagoas" },
			{ key: "AP", value: "Amapá" },
			{ key: "AM", value: "Amazonas" },
			{ key: "BA", value: "Bahia" },
			{ key: "CE", value: "Ceará" },
			{ key: "DF", value: "Distrito Federal" },
			{ key: "ES", value: "Espírito Santo" },
			{ key: "GO", value: "Goiás" },
			{ key: "MA", value: "Maranhão" },
			{ key: "MT", value: "Mato Grosso" },
			{ key: "MS", value: "Mato Grosso do Sul" },
			{ key: "MG", value: "Minas Gerais" },
			{ key: "PA", value: "Pará" },
			{ key: "PB", value: "Paraíba" },
			{ key: "PR", value: "Paraná" },
			{ key: "PE", value: "Pernambuco" },
			{ key: "PI", value: "Piauí" },
			{ key: "RJ", value: "Rio de Janeiro" },
			{ key: "RN", value: "Rio Grande do Norte" },
			{ key: "RS", value: "Rio Grande do Sul" },
			{ key: "RO", value: "Rondônia" },
			{ key: "RR", value: "Roraima" },
			{ key: "SC", value: "Santa Catarina" },
			{ key: "SP", value: "São Paulo" },
			{ key: "SE", value: "Sergipe" },
			{ key: "TO", value: "Tocantins" }
		];
	}

	// BusinessHours
	// ---------------------------

	getBusinessHours(estabId: number): Promise<Array<BusinessHour>> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ estabId: estabId }, APP_CONFIG.WEBSERVICE.GET_BUSINESS_HOURS);
			this.requestService.getData(urlRequest).then((response: Array<BusinessHour>) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	saveBusinessHours(establishmentId: number, businessHours: Array<BusinessHour>): Promise<any> {
		return new Promise((resolve, reject) => {
			const businessHoursJson: Array<any> = new Array();
			businessHours.forEach((b: BusinessHour) => {
				if (b.selected) {
					businessHoursJson.push(b.toJson());
				}
			});
			const data: any = {
				businessHours: businessHoursJson,
				establishmentId: establishmentId
			};
			this.requestService.postData(APP_CONFIG.WEBSERVICE.SAVE_BUSINESS_HOURS, data).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	// Tables
	// ---------------------------

	getTables(estabId: number): Promise<Array<TableListModel>> {
		return new Promise((resolve, reject) => {
			const urlRequest: string = this.requestService.buildUrlPathParams({ estabId: estabId }, APP_CONFIG.WEBSERVICE.GET_TABLES);
			this.requestService.getData(urlRequest).then((response: any) => {
				console.log(response);
				const retorno: Array<TableListModel> = [];
				if (response) {
					//Formato o response para mostrar segmentado por Setor, convertendo para tipo TableListModel
					response.forEach((table: Table) => {
						const sector: string = (table.sector || "Geral").toUpperCase().trim();
						let sectorList: TableListModel;
						const sectorIndex: number = retorno.findIndex((t: TableListModel) => {
							return t.sector === sector;
						});
						//Verifica se é um setor já existente ou um novo
						if (sectorIndex >= 0) {
							sectorList = retorno[sectorIndex];
						} else {
							sectorList = new TableListModel(sector, []);
						}
						sectorList.list.push({
							id: table.id,
							establishmentId: table.establishmentId,
							sector: table.sector,
							mainIdentification: String(table.mainIdentification).toUpperCase().trim(),
							qrCode: table.qrCode
						});
						sectorList.list.sort((a, b) => {
							const identificationA: number | string = UtilHelper.getNumericValueIfPossible(a.mainIdentification);
							const identificationB: number | string = UtilHelper.getNumericValueIfPossible(b.mainIdentification);
							return identificationA > identificationB ? 1 : -1;
							// return a.mainIdentification > b.mainIdentification ? 1 : -1;
						});
						if (sectorIndex >= 0) {
							retorno[sectorIndex] = sectorList;
						} else {
							retorno.push(sectorList);
						}
					});
				}
				resolve(retorno);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	addTables(tables: Array<TableListModel>): Promise<any> {
		return new Promise((resolve, reject) => {
			const tablesToSend: Array<Table> = [];
			tables.forEach((t: TableListModel) => {
				if (t.list && t.list.length > 0) {
					tablesToSend.push.apply(tablesToSend, t.list);
				}
			});
			this.requestService.postData(APP_CONFIG.WEBSERVICE.TABLES, tablesToSend).then((response: any) => {
				console.log(response);
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	editTable(table: Table): Promise<any> {
		return new Promise((resolve, reject) => {
			this.requestService.putData(APP_CONFIG.WEBSERVICE.TABLES, [table]).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}

	deleteTables(tables: Array<Table>): Promise<any> {
		return new Promise((resolve, reject) => {
			let urlRequest: string = APP_CONFIG.WEBSERVICE.TABLES + "?ids=";
			for (let i: number = 0; i < tables.length; i++) {
				urlRequest += tables[i].id;
				if (i < tables.length - 1) {
					urlRequest += ",";
				}
			}
			this.requestService.deleteData(urlRequest).then((response: any) => {
				resolve(response);
			}, (error: Error) => {
				console.error(error);
				reject(error);
			});
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/pages/store/store.service.ts